import { Button, Drawer, DrawerProps, Select, Table, TableColumnsType, message } from 'antd';
import { useMemo, useState } from 'react';
import {
  PhraseGroupV2Body,
  PhrasesRootData,
  getPublicAccountList,
  getPublicPhraseList,
  importPublicPhrases,
} from 'services/api/phrase';
import { phraseContentRender } from './PhraseTable';
import { useTranslate } from '@tolgee/react';
import useModal from 'hooks/useModal';
import PreviewDrawer from './PreviewDrawer';
import StatusButton from 'components/StatusButton';
import { keyBy } from 'lodash-es';
import { platformLabelMap } from 'services/config/platform';
import toLowerCase from 'utils/toLowerCase';
import { useDebounceEffect } from 'ahooks';
import { css } from '@emotion/css';
import { usePhraseContext } from '../PhraseContext';

function PublicImportDrawer({
  regionRange,
  refreshKey,
  refreshPhraseList,
  ...otherProps
}: DrawerProps & {
  regionRange?: General.UpperCaseRegion[];
  refreshKey?: any;
  refreshPhraseList?: () => void;
}) {
  const { t } = useTranslate();

  const { platformList } = usePhraseContext();

  const [selectedRows, setSelectedRows] = useState<PhrasesRootData[]>([]);

  const previewModal = useModal<{
    phraseGroupList: Phrase.Group[];
    phraseRootData: PhrasesRootData;
  }>();

  const [filter, setFilter] = useState<{
    assistantId?: string[];
    platform?: General.PlatformUpperCase[];
    phraseGroupId?: string[];
  }>({});

  const [loading, setLoading] = useState(false);

  const [accountList, setAccountList] = useState<
    {
      name: string;
      userId: string;
      enable: boolean;
    }[]
  >([]);
  const [_groupList, setGroupList] = useState<(PhraseGroupV2Body & { userId: string })[]>([]);
  const groupList = useMemo(() => {
    return _groupList.filter(({ channel }) => platformList.includes(channel));
  }, [_groupList, platformList]);
  const [_phraseList, setPhraseList] = useState<PhrasesRootData[]>([]);
  const phraseList = useMemo(() => {
    return _phraseList.filter(({ channel }) => platformList.includes(channel));
  }, [_phraseList, platformList]);

  const { accountMap, accountOptions, groupMap, groupOptions, platformOptions } = useMemo(() => {
    return {
      accountMap: keyBy(accountList, 'userId'),
      accountOptions: accountList
        .filter(({ userId }) => groupList.some((_) => _.userId === userId))
        .map(({ userId, name }) => ({
          value: userId,
          label: name,
        })),
      groupMap: keyBy(groupList, 'id'),
      groupOptions: groupList.map(({ id, name }) => ({
        value: id,
        label: name,
      })),
      platformOptions: groupList
        .reduce<General.PlatformUpperCase[]>((res, { channel }) => {
          if (!res.includes(channel) && channel !== 'WA_BIZ') {
            res.push(channel);
          }
          return res;
        }, [])
        .map((platform) => ({ value: platform, label: platformLabelMap[toLowerCase(platform)] })),
    };
  }, [accountList, groupList]);

  useDebounceEffect(() => {
    setFilter({});
    setSelectedRows([]);
    setLoading(true);
    Promise.all([getPublicAccountList(), getPublicPhraseList()])
      .then(([accountList, { phraseGroups, phraseRoots }]) => {
        setAccountList(accountList);
        setGroupList(phraseGroups);
        setPhraseList(phraseRoots);
      })
      .catch(() => {
        message.error(t('phrase.获取数据失败，请稍后再试'));
      })
      .finally(() => {
        setLoading(false);
      });
  }, [refreshKey]);

  useDebounceEffect(
    () => {
      setLoading(true);
      getPublicPhraseList(filter)
        .then(({ phraseRoots }) => {
          setPhraseList(phraseRoots);
        })
        .catch(() => {
          message.error(t('phrase.获取数据失败，请稍后再试'));
        })
        .finally(() => {
          setLoading(false);
        });
    },
    [filter],
    { wait: 1500 },
  );

  const columns: TableColumnsType<PhrasesRootData> = [
    {
      key: 'nick',
      title: t('field.name'),
      render: (_, { phraseGroupId }) => {
        const group = groupMap[phraseGroupId];
        const nick = accountMap[group?.userId]?.name;
        return nick || '-';
      },
    },
    {
      key: 'platform',
      title: t('平台'),
      render: (_, { channel }) => platformLabelMap[toLowerCase(channel)],
    },
    {
      key: 'groupName',
      dataIndex: 'groupName',
      title: t('phrase.分组'),
    },
    {
      key: 'content',
      title: t('phrase.快捷回复'),
      render: (_, record) => phraseContentRender(record, t),
    },
    {
      key: 'regions',
      title: t('phrase.站点'),
      render: (_, { phraseMap }) => Object.keys(phraseMap).join('|'),
    },
    {
      key: 'operation',
      title: t('操作'),
      render: (_, record) => (
        <Button
          type="link"
          onClick={() => {
            previewModal.show({
              phraseGroupList: [],
              phraseRootData: record,
            });
          }}
        >
          {t('aide.查看')}
        </Button>
      ),
    },
  ];

  return (
    <Drawer
      width={1200}
      title={t('从公开快捷回复导入')}
      className={className}
      footer={
        <div>
          <StatusButton
            disabled={!selectedRows.length}
            type="primary"
            onClick={(e) => {
              if (!selectedRows.length) {
                message.error(t('请选择导入内容'));
                return Promise.resolve();
              }
              return importPublicPhrases({
                phrase: selectedRows,
              })
                .then(() => {
                  message.success(t('department.已导入'));
                  otherProps.onClose?.(e as any);
                  refreshPhraseList?.();
                })
                .catch(() => {
                  message.error(t('phrase.保存失败，请稍后再试'));
                });
            }}
          >
            {t('bs_14')}
          </StatusButton>
          <Button onClick={otherProps.onClose as any}>{t('取消')}</Button>
          <span>{t('aide.已选x项', { x: selectedRows.length })}</span>
        </div>
      }
      {...otherProps}
    >
      <header>
        <Select
          allowClear
          showArrow
          mode="multiple"
          placeholder={t('department.请选择x', { x: t('field.name') })}
          value={filter.assistantId}
          onChange={(assistantId) => setFilter((filter) => ({ ...filter, assistantId }))}
          options={accountOptions}
        />
        <Select
          allowClear
          showArrow
          mode="multiple"
          placeholder={t('department.请选择x', { x: t('平台') })}
          value={filter.platform}
          onChange={(platform) => setFilter((filter) => ({ ...filter, platform }))}
          options={platformOptions}
        />
        <Select
          allowClear
          showArrow
          mode="multiple"
          placeholder={t('department.请选择x', { x: t('phrase.分组') })}
          value={filter.phraseGroupId}
          onChange={(phraseGroupId) => setFilter((filter) => ({ ...filter, phraseGroupId }))}
          options={groupOptions}
        />
      </header>
      <Table
        loading={loading}
        dataSource={phraseList}
        columns={columns}
        rowKey="id"
        rowSelection={{
          selectedRowKeys: selectedRows.map(({ id }) => id),
          onChange: (_, rows) => setSelectedRows(rows),
        }}
        scroll={{ y: 'max' }}
        pagination={{
          pageSizeOptions: ['10', '20', '50'],
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total) => t('phrase.共x条', { x: total }),
        }}
      />
      {previewModal.meta && (
        <PreviewDrawer
          visible={previewModal.visible}
          onClose={() => previewModal.hide()}
          regionRange={regionRange}
          {...previewModal.meta}
        />
      )}
    </Drawer>
  );
}

const className = css`
  .ant-drawer-content-wrapper {
    max-width: 100vw;
  }
  .ant-drawer-body {
    > header {
      > * {
        min-width: 160px;
        margin-right: 16px;
        margin-bottom: 18px;
      }
    }
  }
  .ant-drawer-footer > div {
    > * {
      margin-right: 16px;
    }
    > div {
      color: var(--fett-text-tertiary);
    }
  }
`;

export default PublicImportDrawer;
